import { Card } from '../ui/Card.jsx';
import { Button } from '../ui/Button.jsx';
import { BUILDINGS } from '../../config/buildings.js';
import { canUnlockBuilding } from '../../systems/buildingSystem.js';
import { useEffect, useRef } from 'react';
import { useAudio } from '../../hooks/useAudio.js';

export function BuildCard({
  buildings, onSelect, onCancel,
  wood, stone, metal,
  day, techPoints, knowledge, morale, workers, raidsDefeated
}) {
  const audio = useAudio();
  const dialogRef = useRef(null);
  const firstBuildingRef = useRef(null);
  const previousFocusRef = useRef(null);

  // Play open sound when modal mounts
  useEffect(() => {
    audio.playSound('ui_open');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const availableBuildings = Object.entries(BUILDINGS).filter(([type]) =>
    !buildings.some(b => b.type === type)
  );

  // Open dialog when component mounts
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    // Save the currently focused element (should be the Assign button)
    previousFocusRef.current = document.activeElement;

    // Show the dialog as a modal
    dialog.showModal();

    // Focus first affordable building after dialog opens
    if (firstBuildingRef.current) {
      firstBuildingRef.current.focus();
    }

    const restoreFocusAndClose = () => {
      audio.playSound('ui_close');
      // Restore focus before calling onCancel
      if (previousFocusRef.current && previousFocusRef.current.focus) {
        previousFocusRef.current.focus();
      }
      onCancel();
    };

    const handleCancel = (e) => {
      // Cancel event fires when user presses Escape
      e.preventDefault(); // Prevent default close
      restoreFocusAndClose();
    };

    const handleBackdropClick = (e) => {
      // Click on backdrop (outside dialog content)
      const rect = dialog.getBoundingClientRect();
      if (
        e.clientX < rect.left ||
        e.clientX > rect.right ||
        e.clientY < rect.top ||
        e.clientY > rect.bottom
      ) {
        restoreFocusAndClose();
      }
    };

    // Listen for Escape key via cancel event (more reliable than 'close')
    dialog.addEventListener('cancel', handleCancel);
    dialog.addEventListener('click', handleBackdropClick);

    // Cleanup
    return () => {
      dialog.removeEventListener('cancel', handleCancel);
      dialog.removeEventListener('click', handleBackdropClick);
      // Don't call dialog.close() here - let unmounting handle it
    };
  }, [onCancel]);

  const handleSelectBuilding = (type) => {
    // Restore focus before calling onSelect
    if (previousFocusRef.current && previousFocusRef.current.focus) {
      previousFocusRef.current.focus();
    }
    onSelect(type);
  };

  return (
    <dialog
      ref={dialogRef}
      className="backdrop:bg-black backdrop:bg-opacity-50 rounded-lg p-0 max-w-md"
    >
      <Card title="Choose Building" icon="🏗️">
        <div className="space-y-3 mb-4" role="list" aria-label="Available buildings">
          {availableBuildings.map(([type, building], index) => {
            // Check unlock conditions using buildingSystem
            const unlockStatus = canUnlockBuilding(type, {
              day, buildings, techPoints, knowledge, morale, workers, raidsDefeated
            });

            // Check resource affordability
            const canAfford = unlockStatus.unlocked &&
              wood >= building.wood &&
              stone >= building.stone &&
              metal >= building.metal;

            // Find first affordable building for auto-focus
            const isFirstAffordable = canAfford &&
              availableBuildings.findIndex(([t, b]) => {
                const status = canUnlockBuilding(t, { day, buildings, techPoints, knowledge, morale, workers, raidsDefeated });
                return status.unlocked && wood >= b.wood && stone >= b.stone && metal >= b.metal;
              }) === index;

            return (
              <button
                key={type}
                ref={isFirstAffordable ? firstBuildingRef : null}
                onClick={() => {
                  if (canAfford) {
                    audio.playSound('ui_confirm');
                    audio.playSound('ui_close');
                    handleSelectBuilding(type);
                  }
                }}
                disabled={!canAfford}
                className={`w-full p-3 rounded-lg border-2 text-left transition-all focus:ring-2 focus:ring-amber-400 focus:outline-none ${
                  canAfford
                    ? 'border-amber-600 bg-amber-100 hover:bg-amber-200 cursor-pointer'
                    : 'border-amber-300 bg-amber-50 opacity-50 cursor-not-allowed'
                }`}
                aria-label={`Build ${building.name}. Cost: ${building.wood} wood, ${building.stone} stone${building.metal > 0 ? `, ${building.metal} metal` : ''}. ${!unlockStatus.unlocked ? `Locked: ${unlockStatus.reason}. ` : ''}${building.benefit}`}
              >
                <div className="font-bold text-amber-900">{building.name}</div>
                <div className="text-sm text-amber-700">
                  Cost: {building.wood > 0 && `🪵${building.wood}`} {building.stone > 0 && `🪨${building.stone}`} {building.metal > 0 && `⚙️${building.metal}`}
                </div>
                {!unlockStatus.unlocked && (
                  <div className="text-sm text-red-600 font-medium">
                    🔒 {unlockStatus.reason}
                  </div>
                )}
                <div className="text-sm text-amber-600">{building.benefit}</div>
              </button>
            );
          })}
        </div>
        <div className="text-center">
          <Button variant="secondary" onClick={() => {
            audio.playSound('ui_close');
            // Restore focus before calling onCancel
            if (previousFocusRef.current && previousFocusRef.current.focus) {
              previousFocusRef.current.focus();
            }
            onCancel();
          }}>Cancel</Button>
        </div>
      </Card>
    </dialog>
  );
}
